import { useEffect, useRef, useState } from "react";
import { ArrowRight, Lock, Check, Shield, Clipboard, Heart, Send, Copy, ChevronDown, Play, Volume2, VolumeX } from "lucide-react";
import { api } from "../lib/api";
import { trackViewContent, trackInitiateCheckout } from "../lib/tiktok";
import ReplyCheckoutModal from "../components/ReplyCheckoutModal";
import InAppBrowserHint from "../components/InAppBrowserHint";

const PRICE = "$9.99";

const FONT = "'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif";
const ensureFont = () => {
  if (document.getElementById("rr-font")) return;
  const l = document.createElement("link");
  l.id = "rr-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(l);
};

// First-party funnel analytics — beacon survives the redirect to Stripe.
const vid = () => {
  try {
    let v = localStorage.getItem("rr_vid");
    if (!v) {
      v = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now()}`;
      localStorage.setItem("rr_vid", v);
    }
    return v;
  } catch (_) { return "anon"; }
};
const track = (event, props = {}) => {
  try {
    const payload = JSON.stringify({ event, props, visitor_id: vid(), page: "landing" });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reply/track`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    else api.post("/reply/track", JSON.parse(payload));
  } catch (_) { /* analytics must never break the funnel */ }
};

// 30s product walkthrough — zero impact on page load: only the 35KB poster is
// fetched up-front (preload="none"); video bytes stream when scrolled into view.
function VideoShowcase({ onCta, loading }) {
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [autoTried, setAutoTried] = useState(false);
  // Fire video_play and video_completed once per visitor session — looping
  // playback or repeat clicks must never inflate the funnel metrics.
  const playedRef = useRef(false);
  const completedRef = useRef(false);

  const handlePlayed = () => {
    if (playedRef.current) return;
    playedRef.current = true;
    track("video_play");
  };
  const handleTimeUpdate = () => {
    const v = vidRef.current;
    if (!v || completedRef.current) return;
    const dur = v.duration;
    // `ended` doesn't fire on loop=true, so we mark completion when the
    // playhead crosses ~98% of the clip — once.
    if (dur && isFinite(dur) && v.currentTime >= dur - 0.4) {
      completedRef.current = true;
      track("video_completed");
    }
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let obs;
    // Attach only after full page load — guarantees the video never competes
    // with initial page resources and the layout is final before we measure.
    const attach = () => {
      obs = new IntersectionObserver(([e]) => {
        const v = vidRef.current;
        if (!v) return;
        if (e.isIntersecting && !autoTried) {
          setAutoTried(true);
          // src is attached only now — guarantees zero video bytes on page load.
          v.src = "/reply-rescue-demo.mp4";
          v.muted = true;
          v.play().then(() => { setPlaying(true); handlePlayed(); }).catch(() => setPlaying(false));
        } else if (!e.isIntersecting && !v.paused) {
          v.pause();
          setPlaying(false);
        }
      }, { threshold: 0.45 });
      obs.observe(el);
    };
    if (document.readyState === "complete") attach();
    else window.addEventListener("load", attach, { once: true });
    return () => {
      window.removeEventListener("load", attach);
      if (obs) obs.disconnect();
    };
  }, [autoTried]);

  const toggle = () => {
    const v = vidRef.current;
    if (!v) return;
    if (v.paused) { v.play().then(() => { setPlaying(true); handlePlayed(); }).catch(() => {}); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const v = vidRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section className="px-5 py-10 sm:py-14" data-testid="reply-video-section">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-extrabold text-[24px] sm:text-3xl tracking-tight">See how Reply Rescue works</h2>
        <p className="mt-3 text-[14px] text-[#C7B8D4] leading-[1.75] max-w-md mx-auto">
          A 30-second walkthrough of how to go from overthinking the text to getting the next move.
        </p>

        <div ref={wrapRef} className="relative mt-8 mx-auto w-full max-w-[300px] sm:max-w-[320px]">
          <div className="absolute inset-0 bg-[#D9707F]/[0.16] blur-3xl rounded-full scale-90 pointer-events-none" aria-hidden />
          {/* phone frame */}
          <div className="relative rounded-[2.2rem] border border-white/[0.10] bg-[#1E0E29] p-2.5 shadow-[0_24px_70px_-18px_rgba(0,0,0,0.7)]">
            <div className="relative rounded-[1.7rem] overflow-hidden aspect-[9/16] bg-black cursor-pointer" onClick={toggle} data-testid="reply-video-frame">
              <video
                ref={vidRef}
                poster="/reply-video-poster.jpg"
                preload="none"
                muted
                loop
                playsInline
                onPlay={handlePlayed}
                onTimeUpdate={handleTimeUpdate}
                className="w-full h-full object-cover"
                data-testid="reply-video"
              />
              {!playing && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <span className="w-16 h-16 rounded-full bg-gradient-to-r from-[#EFA98F] to-[#D9707F] flex items-center justify-center shadow-[0_10px_36px_-8px_rgba(217,112,127,0.8)]" data-testid="reply-video-play">
                    <Play className="w-6 h-6 text-[#2B1230] ml-1" fill="#2B1230" />
                  </span>
                </div>
              )}
              {playing && (
                <button
                  onClick={toggleMute}
                  className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/55 backdrop-blur flex items-center justify-center"
                  data-testid="reply-video-mute"
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted ? <VolumeX className="w-4 h-4 text-white/85" /> : <Volume2 className="w-4 h-4 text-white/85" />}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <button
            onClick={() => onCta()}
            disabled={!!loading}
            data-testid="reply-video-cta"
            className="w-full sm:w-auto bg-gradient-to-r from-[#EFA98F] to-[#D9707F] hover:from-[#f2b39a] hover:to-[#e07e8c] text-[#2B1230] font-extrabold text-base px-8 py-4 rounded-full transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2 min-h-[54px] shadow-[0_10px_40px_-8px_rgba(217,112,127,0.55)]"
          >
            {loading ? "Opening checkout..." : <>Start My Reply Session — {PRICE} <ArrowRight className="w-4 h-4" /></>}
          </button>
          <div className="mt-3 text-[12px] text-[#A795B8]">Private web chat · 5 replies included · No subscription</div>
        </div>
      </div>
    </section>
  );
}

export default function LandingReply() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Inline payment modal — replaces the redirect-to-Stripe flow. State is
  // {pkg|null}; null hides the modal. The Stripe Payment Element renders the
  // PaymentIntent client_secret returned by /reply/payment-intent.
  const [modalPkg, setModalPkg] = useState(null);

  useEffect(() => {
    ensureFont();
    document.title = "Reply Rescue — Get the Next Text";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Paste the chat and get a private AI-powered reply session for confusing texts, mixed signals, dry replies, and difficult conversations.");
    let icon = document.querySelector("link[rel='icon']");
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = "/reply-rescue-icon.png";
    trackViewContent("reply_rescue_5credits");
    track("page_view");
  }, []);

  // Time on site — sent once when the visitor leaves or hides the tab.
  useEffect(() => {
    const start = Date.now();
    let sent = false;
    const send = () => {
      if (sent) return;
      const seconds = Math.round((Date.now() - start) / 1000);
      if (seconds < 2) return;
      sent = true;
      track("time_on_site", { seconds });
    };
    const onHide = () => { if (document.visibilityState === "hidden") send(); };
    window.addEventListener("pagehide", send);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", send);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, []);

  // Opens the inline-payment modal instead of redirecting to Stripe's hosted
  // page. Conversion is significantly higher on MENA markets when the visitor
  // never leaves the branded landing — see /admin/funnel for live data.
  const startCheckout = async (pkg = "reply_rescue_5credits") => {
    setErr("");
    trackInitiateCheckout(pkg);
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function" && !window.__rr_atc_fired) {
        window.__rr_atc_fired = true;
        window.gtag("event", "conversion", {
          send_to: "AW-18239642780/L2brCPLJhL8cEJy5q_lD",
          value: 1.0,
          currency: "EGP",
        });
      }
    } catch (_) { /* analytics must never break the funnel */ }
    track("cta_click", { package: pkg });
    // No more `await api.post('/reply/checkout')` + window.location redirect.
    // The modal will POST /reply/payment-intent itself when it mounts.
    setModalPkg(pkg);
  };

  // Pixel + analytics fired right before stripe.confirmPayment runs — captures
  // the conversion attempt even if 3DS / Apple-Pay flow drops the customer
  // off after they leave the modal.
  const onModalAddToCart = () => {
    try { trackInitiateCheckout(modalPkg || "reply_rescue_5credits"); } catch (_) { /* never breaks */ }
    try { track("payment_submit", { package: modalPkg }); } catch (_) { /* never breaks */ }
  };

  return (
    <div
      dir="ltr"
      lang="en"
      className="min-h-screen bg-[#15081C] text-[#F6EFE7] selection:bg-[#D9707F]/40"
      style={{ fontFamily: FONT, textAlign: "left" }}
      data-testid="reply-landing-root"
    >
      <InAppBrowserHint />
      {/* NAV */}
      <nav className="px-5 pt-5 max-w-6xl mx-auto flex items-center justify-between">
        <img src="/reply-rescue-logo.png" alt="Reply Rescue" className="h-8 sm:h-9 w-auto" data-testid="reply-logo-badge" />
        <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-[#A795B8]">
          <Lock className="w-3 h-3 text-[#E8A18F]" /> Private · No account needed
        </div>
      </nav>

      {/* HERO — split on desktop */}
      <header className="px-5 pt-8 sm:pt-12 pb-10 sm:pb-16 relative overflow-hidden" data-testid="reply-hero">
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#D9707F]/[0.13] blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute top-64 -left-40 w-[360px] h-[360px] rounded-full bg-[#7B5C9E]/[0.12] blur-3xl pointer-events-none" aria-hidden />
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center relative">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E8A18F]/[0.08] border border-[#E8A18F]/25 text-[11px] tracking-[0.18em] text-[#E8A18F] font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E8A18F] animate-pulse" /> PRIVATE TEXT-REPLY ASSISTANT
            </div>
            <h1 className="font-extrabold text-[34px] sm:text-5xl lg:text-[54px] leading-[1.12] tracking-tight">
              They replied cold.
              <br />
              <span className="bg-gradient-to-r from-[#F0B49A] via-[#E08D92] to-[#D9707F] bg-clip-text text-transparent">
                Don&apos;t answer yet.
              </span>
            </h1>
            <p className="mt-4 text-[15px] sm:text-base text-[#C7B8D4] leading-[1.75] max-w-md">
              Paste the message. Choose your goal. Get a reply that sounds <span className="text-white font-semibold">calm, confident, and natural.</span>
            </p>
            <div className="mt-7">
              <button
                onClick={() => startCheckout()}
                disabled={!!loading}
                data-testid="reply-hero-cta"
                className="w-full sm:w-auto bg-gradient-to-r from-[#EFA98F] to-[#D9707F] hover:from-[#f2b39a] hover:to-[#e07e8c] text-[#2B1230] font-extrabold text-base px-8 py-4 rounded-full transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-h-[54px] shadow-[0_10px_40px_-8px_rgba(217,112,127,0.55)]"
              >
                {loading ? "Opening checkout..." : <>Get 5 Replies — {PRICE} <ArrowRight className="w-4 h-4" /></>}
              </button>
              <div className="mt-3 text-[12px] text-[#A795B8]">
                Private web chat · 5 replies included · Instant access · No subscription
              </div>
              {err && <div className="mt-3 text-[13px] text-[#F0A3A3]" data-testid="checkout-err">{err}</div>}
            </div>
            {/* 4-step inline how-it-works — appears immediately after the CTA */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="reply-hero-steps">
              {[
                { n: "01", t: "Paste the chat" },
                { n: "02", t: "Choose tone" },
                { n: "03", t: "Get reply" },
                { n: "04", t: "If they reply back, paste it again" },
              ].map((s, i) => (
                <div
                  key={s.n}
                  className="relative bg-white/[0.035] border border-white/[0.09] rounded-2xl px-3.5 py-3 hover:border-[#D9707F]/45 transition-colors"
                  data-testid={`reply-hero-step-${i + 1}`}
                >
                  <div className="text-[9.5px] tracking-[0.22em] text-[#E8A18F] font-bold mb-1">STEP {s.n}</div>
                  <div className="text-[12.5px] text-[#EFE6F2] font-semibold leading-snug">{s.t}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — product mockup */}
          <div className="relative max-w-[360px] mx-auto w-full lg:max-w-[400px]" data-testid="reply-hero-mockup">
            <div className="absolute inset-0 bg-[#D9707F]/[0.18] blur-3xl rounded-full scale-90 pointer-events-none" aria-hidden />
            {/* decorative blurred bubbles */}
            <div className="absolute -top-5 -left-4 w-24 h-10 rounded-2xl bg-white/[0.04] blur-[2px] rotate-[-6deg] pointer-events-none" aria-hidden />
            <div className="absolute -bottom-4 -right-3 w-28 h-10 rounded-2xl bg-[#D9707F]/[0.08] blur-[2px] rotate-[5deg] pointer-events-none" aria-hidden />
            <div className="relative bg-[#1E0E29]/95 backdrop-blur border border-white/[0.09] rounded-[28px] p-5 shadow-[0_24px_70px_-18px_rgba(0,0,0,0.7)]">
              <div className="flex items-center justify-between mb-4">
                <img src="/reply-rescue-logo.png" alt="" className="h-5 w-auto opacity-90" />
                <span className="text-[10px] text-[#A795B8] flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Private session</span>
              </div>

              <div className="text-[10px] tracking-[0.2em] text-[#A795B8] font-semibold mb-1.5">THEIR MESSAGE</div>
              <div className="bg-white/[0.07] border border-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3 text-[13.5px] text-[#EFE6F2] w-fit max-w-[85%]">
                She followed you back on Instagram
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-[11.5px] px-3 py-1.5 rounded-full bg-[#D9707F] text-[#2B1230] font-bold flex items-center gap-1">
                  <Check className="w-3 h-3" strokeWidth={3} /> Start smooth
                </span>
                <span className="text-[11.5px] px-3 py-1.5 rounded-full border border-[#E8A18F]/40 text-[#E8A18F] font-semibold">
                  Sound confident
                </span>
              </div>

              <div className="mt-4 bg-gradient-to-br from-[#D9707F]/[0.12] to-transparent border border-[#D9707F]/40 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] tracking-[0.2em] text-[#E8A18F] font-bold flex items-center gap-1.5">
                    <Heart className="w-3 h-3 fill-[#E8A18F]" /> REPLY RESCUE SUGGESTS
                  </span>
                  <span className="text-[10px] bg-white/10 px-2 py-1 rounded-md text-[#E9DCEF] flex items-center gap-1"><Copy className="w-2.5 h-2.5" /> Copy</span>
                </div>
                <p className="text-[13.5px] text-[#F6EFE7] leading-relaxed">
                  You give off that rare mix of confidence and softness. Has anyone said that to you before?
                </p>
              </div>

              <div className="mt-3 text-[11px] text-[#A795B8] text-center">1 credit used · 4 replies left</div>
            </div>

            {/* What this reply does */}
            <div className="mt-4 bg-white/[0.03] border border-white/[0.07] rounded-2xl px-4 py-3.5" data-testid="reply-mockup-why">
              <div className="text-[10px] tracking-[0.2em] text-[#A795B8] font-bold mb-2">WHAT THIS REPLY DOES</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {["Keeps you calm", "Avoids chasing", "Sets a boundary", "Makes them be specific"].map((t) => (
                  <span key={t} className="text-[11.5px] text-[#E9DCEF] flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-[#E8A18F]" strokeWidth={3} /> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* VIDEO WALKTHROUGH */}
      <VideoShowcase onCta={startCheckout} loading={loading} />

      {/* PAIN — bubble cards */}
      <section className="px-5 py-12 sm:py-16" data-testid="reply-pain">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-extrabold text-[26px] sm:text-4xl leading-tight tracking-tight text-center">
            You don’t need another dating course.
            <br />
            <span className="bg-gradient-to-r from-[#F0B49A] to-[#D9707F] bg-clip-text text-transparent">You need the next reply.</span>
          </h2>
          <p className="mt-5 text-[14px] sm:text-[15px] text-[#C7B8D4] leading-[1.8] text-center max-w-xl mx-auto">
            When someone replies cold, disappears, cancels, comes back, or sends a confusing
            message, the wrong reply can make you look angry, needy, or too available.
            Reply Rescue helps you slow down and send something clear, calm, and confident.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { t: "They replied dry", tail: "rounded-bl-md" },
              { t: "They left you on read", tail: "rounded-br-md" },
              { t: "They came back after disappearing", tail: "rounded-bl-md" },
              { t: "They cancelled again", tail: "rounded-br-md" },
              { t: "You want clarity", tail: "rounded-bl-md" },
              { t: "You don’t want to sound needy", tail: "rounded-br-md" },
            ].map((c, i) => (
              <div
                key={c.t}
                className={`rounded-2xl ${c.tail} px-4 py-4 text-[13px] sm:text-[14px] font-semibold leading-snug ${
                  i % 3 === 1
                    ? "bg-[#D9707F]/[0.09] border border-[#D9707F]/25 text-[#F2DCD9]"
                    : "bg-white/[0.045] border border-white/[0.08] text-[#E9DCEF]"
                }`}
              >
                {c.t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY NOT CHATGPT — objection handling */}
      <section className="px-5 py-12 sm:py-16" data-testid="reply-vs-chatgpt">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-extrabold text-[26px] sm:text-4xl text-center tracking-tight">
            Why not just ask <span className="bg-gradient-to-r from-[#F0B49A] to-[#D9707F] bg-clip-text text-transparent">ChatGPT?</span>
          </h2>
          <p className="mt-4 text-[14px] sm:text-[15px] text-[#C7B8D4] leading-[1.8] text-center max-w-xl mx-auto">
            ChatGPT gives you a blank box. Reply Rescue gives you a focused reply
            session built for real texting situations.
          </p>
          <div className="mt-9 grid sm:grid-cols-2 gap-4 items-stretch">
            {/* ChatGPT card */}
            <div className="bg-white/[0.03] border border-white/[0.09] rounded-3xl p-6" data-testid="reply-vs-card-chatgpt">
              <div className="font-bold text-[15px] text-[#A795B8] mb-4">ChatGPT</div>
              <ul className="space-y-2.5 text-[13.5px] text-[#B5A4C4] leading-relaxed">
                {[
                  "You have to explain the situation yourself",
                  "You have to write the right prompt",
                  "Replies can sound generic or too long",
                  "It may miss the emotional risk",
                  "You still have to decide what to send",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6E5A80] shrink-0 mt-2" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Reply Rescue card */}
            <div className="bg-gradient-to-b from-[#2A1430] to-[#1E0E27] border border-[#D9707F]/50 rounded-3xl p-6 shadow-[0_0_44px_-14px_rgba(217,112,127,0.4)]" data-testid="reply-vs-card-rr">
              <div className="font-bold text-[15px] mb-4 flex items-center gap-2">
                <Heart className="w-4 h-4 text-[#E8A18F] fill-[#E8A18F]" />
                <span className="bg-gradient-to-r from-[#F0B49A] to-[#D9707F] bg-clip-text text-transparent">Reply Rescue</span>
              </div>
              <ul className="space-y-2.5 text-[13.5px] text-[#EFE6F2] leading-relaxed">
                {[
                  "Choose the situation",
                  "Choose your goal",
                  "Choose the tone",
                  "Get a ready-to-send reply",
                  "See what not to send",
                  "Know what to do if they reply, go cold, or disappear",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-[#E8A18F] shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-7 text-[13.5px] sm:text-[14.5px] text-[#E9DCEF] text-center max-w-xl mx-auto leading-[1.8] font-semibold">
            Reply Rescue is not a general chatbot. It is a private decision flow for
            the exact moment before you send the next text.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-5 py-12 sm:py-14 bg-[#1A0C23] border-y border-white/[0.05]" data-testid="reply-how">
        <div className="max-w-4xl mx-auto">
          <div className="text-[11px] text-[#E8A18F] tracking-[0.3em] font-bold mb-3 text-center">HOW IT WORKS</div>
          <h2 className="font-extrabold text-[24px] sm:text-3xl text-center mb-9 tracking-tight">Paste. Choose. Send.</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: Clipboard, t: "Paste the conversation", d: "Just the part that matters. Remove names and private details." },
              { icon: Heart, t: "Choose your goal", d: "Keep it going, ask for clarity, set a boundary, reply playfully, or walk away." },
              { icon: Send, t: "Get your reply", d: "A ready-to-send message, two alternatives, and what NOT to send." },
              { icon: Check, t: "Send with confidence", d: "Plus your next move if they answer warm, cold, or not at all." },
            ].map((s, i) => (
              <div key={s.t} className="bg-white/[0.035] border border-white/[0.08] rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-[#D9707F]/[0.14] border border-[#D9707F]/30 flex items-center justify-center mb-3">
                  <s.icon className="w-4 h-4 text-[#E8A18F]" />
                </div>
                <div className="text-[10px] text-[#A795B8] font-bold mb-1">STEP {i + 1}</div>
                <div className="font-bold text-[14px] leading-snug">{s.t}</div>
                <p className="mt-1.5 text-[12px] text-[#B5A4C4] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="px-5 py-12" data-testid="reply-what">
        <div className="max-w-3xl mx-auto">
          <div className="text-[11px] text-[#E8A18F] tracking-[0.3em] font-bold mb-3 text-center">EVERY REPLY INCLUDES</div>
          <h2 className="font-extrabold text-[24px] sm:text-3xl text-center mb-8 tracking-tight">What you get</h2>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 sm:p-7">
            <ul className="space-y-3 text-[14px] text-[#EFE6F2] leading-relaxed">
              {[
                "A ready-to-send reply",
                "2 alternative reply styles (softer / more confident)",
                "Why this reply works",
                "What not to send",
                "What to do if they reply",
                "What to do if they don't reply",
                "5 reply credits — one Rescue Session",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#D9707F]/[0.15] border border-[#D9707F]/40 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#E8A18F]" strokeWidth={3} />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* A GLIMPSE — conversation-style sample */}
      <section className="px-5 py-12" data-testid="reply-glimpse">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-extrabold text-[24px] sm:text-3xl text-center tracking-tight">
            A glimpse of <span className="bg-gradient-to-r from-[#F0B49A] to-[#D9707F] bg-clip-text text-transparent">Reply Rescue</span>
          </h2>
          <p className="mt-4 text-[13.5px] sm:text-[14.5px] text-[#C7B8D4] leading-[1.8] text-center max-w-lg mx-auto">
            A peek at the kind of grown, no-nonsense voice your replies will carry.
          </p>

          <div className="mt-8 bg-gradient-to-b from-[#2A1430] to-[#1E0E27] border border-[#D9707F]/45 rounded-3xl p-5 sm:p-6 shadow-[0_0_44px_-14px_rgba(217,112,127,0.4)]" data-testid="reply-glimpse-card">
            <div className="text-[10px] tracking-[0.2em] text-[#E8A18F] font-bold mb-4 flex items-center gap-1.5">
              <Heart className="w-3 h-3 fill-[#E8A18F]" /> REPLY RESCUE
            </div>
            <div className="space-y-2.5">
              {[
                "Love isn't usually the problem.",
                "People think every married man who's talking to another woman must hate his wife, be unhappy, or be planning an escape.",
                "Life is rarely that dramatic.",
                "Sometimes you can care about someone, build a life with them, and still enjoy the attention of someone new who knows absolutely nothing about you.",
                "Someone unpredictable.",
                "Someone who asks questions she's probably not supposed to ask. 😏",
              ].map((line, i) => (
                <div
                  key={i}
                  className={`bg-[#D9707F]/[0.12] border border-[#D9707F]/30 rounded-2xl rounded-br-md px-4 py-3 text-[14px] text-[#F6EFE7] leading-[1.65] max-w-[92%] ml-auto`}
                  data-testid={`reply-glimpse-bubble-${i}`}
                >
                  {line}
                </div>
              ))}
            </div>
            <div className="mt-5 text-[12px] text-[#E8A18F] font-semibold flex items-center justify-end gap-1.5">
              <Check className="w-3.5 h-3.5" strokeWidth={3} /> Grown. Calm. Unbothered.
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="px-5 py-12 sm:py-16 bg-[#1A0C23] border-y border-white/[0.05]" data-testid="reply-pricing" id="pricing">
        <div className="max-w-4xl mx-auto">
          <div className="text-[11px] text-[#E8A18F] tracking-[0.3em] font-bold mb-3 text-center">CHOOSE YOUR SESSION</div>
          <h2 className="font-extrabold text-[24px] sm:text-3xl text-center mb-10 tracking-tight">One-time payment. No subscription.</h2>
          <div className="grid sm:grid-cols-3 gap-4 items-stretch">
            {[
              {
                key: "reply_rescue_5credits", title: "Rescue Session", price: "$9.99",
                unit: "5 reply credits", desc: "Best for one active conversation.",
                badge: "MOST POPULAR", highlighted: true, cta: "Start 5-Reply Session",
              },
              {
                key: "reply_rescue_15credits", title: "Deep Rescue", price: "$19.99",
                unit: "15 reply credits", desc: "Best for ongoing conversations — 33% cheaper per reply.",
                badge: "BEST VALUE", highlighted: false, cta: "Start 15-Reply Session",
              },
              {
                key: "reply_rescue_24h_pass", title: "24h Reply Pass", price: "$14.99",
                unit: "Unlimited for 24 hours", desc: "Reply freely all day. Fair use: 40 replies.",
                badge: null, highlighted: false, cta: "Start 24h Pass",
              },
            ].map((p) => (
              <div
                key={p.key}
                className={`rounded-3xl p-6 text-center relative flex flex-col transition-shadow ${
                  p.highlighted
                    ? "bg-gradient-to-b from-[#2A1430] to-[#1E0E27] border border-[#D9707F]/60 shadow-[0_0_50px_-12px_rgba(217,112,127,0.45)] sm:-mt-2 sm:mb-[-8px]"
                    : "bg-white/[0.03] border border-white/[0.09]"
                }`}
                data-testid={`reply-pricing-card-${p.key}`}
              >
                {p.badge && (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-extrabold px-3.5 py-1 rounded-full tracking-wider ${
                    p.highlighted ? "bg-gradient-to-r from-[#EFA98F] to-[#D9707F] text-[#2B1230]" : "bg-white/12 text-[#E9DCEF]"
                  }`}>{p.badge}</span>
                )}
                <div className="font-bold text-[15px] text-[#E9DCEF]">{p.title}</div>
                <div className={`font-extrabold text-4xl mt-2 tracking-tight ${p.highlighted ? "text-[#E8A18F]" : "text-[#F6EFE7]"}`}>{p.price}</div>
                <div className="text-[12px] text-[#A795B8] mt-1">one-time</div>
                <div className="mt-4 font-bold text-[15px]">{p.unit}</div>
                <div className="text-[12px] text-[#B5A4C4] mt-1 flex-1 leading-relaxed">{p.desc}</div>
                <button
                  onClick={() => startCheckout(p.key)}
                  disabled={!!loading}
                  data-testid={`reply-pricing-cta-${p.key}`}
                  className={`mt-5 w-full font-extrabold text-[14px] px-5 py-3.5 rounded-full transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-h-[48px] ${
                    p.highlighted
                      ? "bg-gradient-to-r from-[#EFA98F] to-[#D9707F] hover:from-[#f2b39a] hover:to-[#e07e8c] text-[#2B1230] shadow-[0_8px_28px_-8px_rgba(217,112,127,0.6)]"
                      : "bg-[#D9707F]/[0.14] hover:bg-[#D9707F]/[0.26] text-[#F6EFE7] border border-[#D9707F]/45 hover:border-[#D9707F]/70 shadow-[0_6px_20px_-10px_rgba(217,112,127,0.45)]"
                  }`}
                >
                  {loading === p.key ? "Opening checkout..." : <>{p.cta} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-5 text-[11px] text-[#A795B8] flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3 text-[#E8A18F]" /> Secure Stripe checkout · One-time payment · Credits never expire
          </div>
        </div>
      </section>

      {/* PRIVACY — calm private note */}
      <section className="px-5 py-12" data-testid="reply-privacy">
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#211130] border border-[#9B7FBC]/20 rounded-3xl p-7 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#7B5C9E]/[0.15] blur-3xl pointer-events-none" aria-hidden />
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-2xl bg-[#9B7FBC]/[0.15] border border-[#9B7FBC]/30 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#C9B8E8]" strokeWidth={1.7} />
              </span>
              <h2 className="font-extrabold text-xl sm:text-2xl tracking-tight">Private by design</h2>
            </div>
            <p className="text-[14px] text-[#D5C8E0] leading-[1.8]">
              No account required. No subscription. Your session opens in a private link
              after payment. Save the page if you want to come back later.
            </p>
            <p className="mt-4 text-[12.5px] text-[#A795B8] leading-relaxed border-t border-white/[0.07] pt-4">
              Remove names, phone numbers, addresses, and sensitive details before pasting.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-5 pb-14" data-testid="reply-faq">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-extrabold text-[24px] sm:text-3xl text-center mb-8 tracking-tight">FAQ</h2>
          <div className="space-y-3">
            {[
              { q: "Is this a subscription?", a: "No. It is a one-time payment. You can buy more replies whenever you want." },
              { q: "What happens after I pay?", a: "You are redirected to your private Reply Rescue page where your credits are already active." },
              { q: "Can I use it again later?", a: "Yes. Save your private page link or use the 'Copy private access link' button." },
              { q: "What if I run out of replies?", a: "You can recharge from inside your private page: another 5-pack ($9.99), a 15-credit Deep Rescue ($19.99), or a 24-hour Reply Pass ($14.99)." },
              { q: "Is this for women or men?", a: "Both. Reply Rescue works for anyone who wants to reply calmly and clearly." },
              { q: "Will it manipulate the other person?", a: "No. Reply Rescue is built for clear, confident communication — not manipulation, pressure, or games." },
            ].map((f, i) => (
              <details key={i} className="group bg-white/[0.035] border border-white/[0.08] rounded-2xl px-5 py-4" data-testid={`reply-faq-${i}`}>
                <summary className="cursor-pointer font-bold text-[14px] list-none flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <ChevronDown className="w-4 h-4 text-[#E8A18F] shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-[13px] text-[#B5A4C4] leading-[1.75]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-8 border-t border-white/[0.05] text-center">
        <img src="/reply-rescue-logo.png" alt="Reply Rescue" className="h-6 w-auto mx-auto mb-3 opacity-70" />
        <p className="text-[11px] text-[#8E7E9E] max-w-md mx-auto leading-relaxed">
          Reply Rescue is an AI-powered communication assistant. It does not guarantee outcomes,
          replies, dates, relationships, or behavior from other people.
        </p>
      </footer>

      {/* MOBILE STICKY CTA */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#15081C]/95 backdrop-blur border-t border-white/[0.08] p-3 flex items-center gap-3">
        <div className="text-[11px] text-[#A795B8] leading-tight">
          <div className="font-bold text-[#F6EFE7]">Reply Rescue</div>
          <div>5 replies · {PRICE}</div>
        </div>
        <button
          onClick={() => startCheckout()}
          disabled={!!loading}
          data-testid="reply-sticky-cta"
          className="ml-auto bg-gradient-to-r from-[#EFA98F] to-[#D9707F] text-[#2B1230] font-extrabold text-sm px-5 py-3 rounded-full flex items-center gap-1.5 min-h-[48px] disabled:opacity-50 shadow-[0_8px_24px_-8px_rgba(217,112,127,0.6)]"
        >
          {loading ? "..." : <>Start Reply Session — {PRICE}</>}
        </button>
      </div>
      <div className="sm:hidden h-20" aria-hidden />

      {/* Inline Stripe Payment Element modal — opens from any CTA. Replaces
          the legacy redirect-to-Stripe flow so visitors stay on the branded
          page. Stripe surfaces Apple Pay / Google Pay / Card automatically. */}
      <ReplyCheckoutModal
        open={!!modalPkg}
        packageKey={modalPkg}
        visitorId={vid()}
        onClose={() => setModalPkg(null)}
        onAddToCart={onModalAddToCart}
      />
    </div>
  );
}
